import { useEffect, useRef, useState } from "react";

//Common
import { DataGrid } from "../../common/VFX-UI";
import PageHeader from "../../common/PageHeader/PageHeader";
import ExportButton from "../../common/Buttons/ExportButton";
import SearchContainer from "../../common/Forms/SearchContainer/SearchContainer";

//App
import { FormikTypeaheadOption } from "../../../types";
import { SearchProps } from "../../../types";
import { SearchDefaultValue } from "../../../store/types";
import {
    EMPTY_GUID,
    SearchFieldsDefaultValue,
} from "../../../utility/constants/constants";
import { CampaignContributionReportProps } from "../../../types";
import useCampaignContributionsReport from "./hooks/useCampaignContribution";
import reportApi from "../../../app/model/api/reportApi";
import useCampaignStyles from "../../Campaign/styles";
import { CampaignContributionReportDto } from "../../../app/model/api/types/dto";


const CampaignContributionReport: React.FC<CampaignContributionReportProps> = (
    props
) => {
    const { data: campaignContributions } = props;
    const [showSearchFields, setShowSearchFields] = useState<boolean>(false);
    const [filterContainerWidth, setFilterContainerWidth] = useState(0);
    const filterContainerRef = useRef<HTMLDivElement>(null);

    const { formContext, rowEvents } = useCampaignContributionsReport({});
    const campaignStyles = useCampaignStyles();

    useEffect(() => {
        const filterContainer = filterContainerRef.current;
        if (!filterContainer) return;

        const resizeObserver = new ResizeObserver(([entry]) => {
            setFilterContainerWidth(entry.contentRect.width);
        });

        resizeObserver.observe(filterContainer);
        return () => resizeObserver.disconnect();
    }, []);

    const handleOnCampaignChange = async (value: FormikTypeaheadOption[]) => {
        let campaignId: string | null = null;
        let organizationId: string | null = null;

        if (value && value?.length > 0) {
            campaignId = (value[0] as Record<string, string | number>)[
                "value"
            ] as string;
            organizationId = (value[0] as Record<string, string | number>)[
                "organizationId"
            ] as string;
            setShowSearchFields(true);
        } else {
            setShowSearchFields(false);
        }
        if (formContext?.id === campaignId) return;
        await formContext?.setOrganizationId(organizationId);
        await formContext?.setId(campaignId);
    };

    const searchProps: SearchProps<CampaignContributionReportDto> = {
        dropdown: {
            options: formContext?.campaigns ?? [],
            defaultSelected: formContext?.id != null ? [formContext.id] : [],
            clearButton: true,
            name: "campaignId",
            label: "Campaigns",
            onChange: (value: any) => handleOnCampaignChange(value),
        },
        searchValue: formContext?.searchValue ?? SearchDefaultValue,
        searchFields: {
            ...SearchFieldsDefaultValue,
            name: showSearchFields,
            personnelNumber: showSearchFields,
            contributorType: showSearchFields,
            responded: showSearchFields,
            hasContributed: showSearchFields,
            methodOfGiving: showSearchFields,
        },
        contributorTypes: formContext?.contributorTypes ?? undefined,
        methodOfGivingTypes: formContext?.methodOfGivingTypes ?? undefined,
        filterLayout: "campaign-contributions",
        wrapFiltersEarly: true,
        breakActionsToNewRow: filterContainerWidth > 0 && filterContainerWidth < 1500,
        setSearchValue: formContext?.setSearchValue,
        refetchGrid: formContext?.refetchGrid,
    };

    const columns = [
        {
            dataField: "personnelNumber",
            text: "Personnel No.",
            sort: true,
        },
        {
            dataField: "name",
            text: "Name",
            sort: true,
        },
        {
            dataField: "contributorType",
            text: "Contributor Type",
            sort: true,
        },
        {
            dataField: "methodOfGiving",
            text: "Method of Giving",
            sort: false,
        },
        {
            dataField: "responded",
            text: "Responded",
            formatter: (cell: any) => {
                return `${cell ? "Yes" : "No"}`;
            },
            sort: false,
        },
        {
            dataField: "isAnonymous",
            text: "Anonymous",
            formatter: (cell: any) => {
                return `${cell ? "Yes" : "No"}`;
            },
            sort: false,
        },
        {
            dataField: "campaignName",
            text: "Campaign",
            sort: false,
        },
        {
            dataField: "campaignCode",
            text: "Campaign Code",
            sort: false,
        },
        //{
        //    dataField: "agency",
        //    text: "Agency",
        //    formatter: (cell: Date, row: CampaignContributionReportDto) => {
        //        const hasOtherAgencyName =
        //            row.otherAgencyName != null && row.otherAgencyName.length > 0;
        //        const otherAgencyNameSuffix = hasOtherAgencyName
        //            ? ` - ${row.otherAgencyName}`
        //            : "";

        //        return `${cell}${otherAgencyNameSuffix}`;
        //    },
        //    sort: false,
        //},

        //{
        //    dataField: "pledgeAmount",
        //    text: "Pledge Amount",
        //    sort: false,
        //},
        {
            dataField: "totalContribution",
            text: "Total Contribution",
            formatter: (cell: any) => {
                return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', }).format(cell);
            },
            sort: false,
        },
    ];

    const exportUrl = reportApi.exportContributions({
        ...formContext?.searchValue,
        campaignId: formContext?.id ?? EMPTY_GUID,
    });

    return (
        <>
        <PageHeader>Campaign Contributions</PageHeader>
        <div style={campaignStyles.searchCampaignContainer}>
            <div ref={filterContainerRef} style={{ flex: 1, minWidth: 0 }}>
                <SearchContainer searchProps={searchProps} />
            </div>
                {campaignContributions?.length !== 0 && campaignContributions && (
                    <div className="mb-3" style={campaignStyles.completedButtonContainer}>
                        <ExportButton method="GET" url={exportUrl} fileName="Campaign Contributions"/>
                    </div>
                )}
            </div>
            <DataGrid
                columns={columns}
                data={campaignContributions ?? []}
                keyField="pledgeId"
                rowEvents={rowEvents}
                totalSize={formContext?.paging?.totalSize}
                defaultSorted={[{
                    dataField: 'personnelNumber',
                    order: 'asc'
                }]}
                remote={true}
                pageSize={formContext?.pageSize}
                page={formContext?.paging?.currentPage}
                handlePageChange={async (newPage, pageSize) => {
                    if (formContext?.setCurrentPage && newPage) {
                        await formContext?.setCurrentPage(newPage);
                    }
                    if (formContext?.setCurrentPageSize && pageSize) {
                        await formContext?.setCurrentPageSize(pageSize);
                    }
                    formContext?.refetchGrid();
                }}
            />
        </>
    );
};

export default CampaignContributionReport;
